import { jsPDF } from "jspdf";
import "jspdf-autotable";

// Nota de remision en blanco y negro, solo texto (sin logos): encabezado de la
// empresa, folio y fecha, datos del cliente y de envio, tabla de productos,
// observaciones, totales y firmas. La usan ListadoSurtidos y ListadoVentas.
//
// datos = {
//   empresa: { nombre, rfc, cp, ciudad, telefono, email },
//   folio, fecha, pedido, factura, ordenCompraCliente,
//   cliente: { nombre, rfc, direccion, telefono },
//   envio: { destinatario, direccion, telefono, ciudadEstado },
//   renglones: [{ descripcion, cantidad, unidad, precio, observaciones }],
//   observaciones, elaboro,
//   totales: { piezas, precio, modelos, total },
//   nombreArchivo,
// }
const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(n) || 0);
const num = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(Number(n) || 0);
const txt = (v) => (v === undefined || v === null ? "" : String(v));

export function notaRemisionPDF(datos) {
  const d = datos || {};
  const emp = d.empresa || {};
  const cli = d.cliente || {};
  const env = d.envio || {};
  const tot = d.totales || {};
  const doc = new jsPDF({ unit: "mm", format: "a4", compressPdf: true });
  const GRIS = [235, 235, 235];
  const NEGRO = [0, 0, 0];
  const L = 10; // margen izquierdo
  const R = 200; // borde derecho
  const ANCHO = R - L;

  doc.setTextColor(...NEGRO);
  doc.setDrawColor(...NEGRO);

  // ---- Encabezado: empresa a la izquierda, folio y fecha a la derecha ----
  doc.setFont("helvetica", "bold");
  doc.setFontSize(26);
  doc.text(txt(emp.nombre || "EMPRESA").toUpperCase(), L, 22);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  let y = 30;
  const linEmp = [
    emp.rfc ? `RFC: ${emp.rfc}` : "",
    [emp.cp ? `C.P. ${emp.cp}` : "", emp.ciudad].filter(Boolean).join(", "),
    [emp.telefono ? `Tel. ${emp.telefono}` : "", emp.email].filter(Boolean).join("  |  "),
  ].filter(Boolean);
  linEmp.forEach((l) => { doc.text(l, L, y); y += 5; });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("NOTA DE REMISIÓN", R, 16, { align: "right" });
  doc.setFontSize(11);
  doc.text("Folio No.", 132, 27);
  doc.setLineWidth(0.5);
  doc.roundedRect(154, 21, 46, 9, 1.5, 1.5);
  doc.setFontSize(12);
  doc.text(txt(d.folio), 177, 27.3, { align: "center" });
  doc.setFontSize(11);
  doc.text("Fecha:", 132, 37);
  doc.setFont("helvetica", "normal");
  doc.text(txt(d.fecha), 154, 37);
  // Referencias opcionales: pedido, factura, orden de compra del cliente
  doc.setFontSize(8.5);
  const refs = [
    d.pedido ? `Pedido: ${d.pedido}` : "",
    d.factura && d.factura !== "Sin Factura" ? `Factura: ${d.factura}` : "",
    d.ordenCompraCliente ? `O.C. cliente: ${d.ordenCompraCliente}` : "",
  ].filter(Boolean);
  if (refs.length) doc.text(refs.join("   "), R, 43, { align: "right" });

  // ---- Cajas: datos del cliente / datos de envio ----
  const yBox = 48;
  const hBox = 46;
  const mitad = L + ANCHO / 2;
  doc.setLineWidth(0.4);
  doc.roundedRect(L, yBox, ANCHO, hBox, 1.5, 1.5);
  doc.line(mitad, yBox, mitad, yBox + hBox);
  doc.setFillColor(...GRIS);
  doc.rect(L + 0.3, yBox + 0.3, ANCHO / 2 - 0.3, 7, "F");
  doc.rect(mitad + 0.3, yBox + 0.3, ANCHO / 2 - 0.6, 7, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.text("DATOS DEL CLIENTE", L + 3, yBox + 5);
  doc.text("DATOS DE ENVÍO", mitad + 3, yBox + 5);

  // Cada campo: etiqueta en normal y valor a continuacion; si el valor no cabe,
  // se corta en lineas dentro de la caja.
  const campo = (x, yy, etiqueta, valor, anchoMax) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.text(etiqueta, x, yy);
    const xv = x + doc.getTextWidth(etiqueta) + 1.5;
    const lineas = doc.splitTextToSize(txt(valor), anchoMax - (xv - x));
    doc.text(lineas.slice(0, 2), xv, yy);
    return yy + 6.5 + (lineas.length > 1 ? 4 : 0);
  };
  const wCol = ANCHO / 2 - 6;
  let yc = yBox + 13;
  yc = campo(L + 3, yc, "Nombre / Razón Social:", cli.nombre, wCol);
  yc = campo(L + 3, yc, "RFC:", cli.rfc, wCol);
  yc = campo(L + 3, yc, "Dirección:", cli.direccion, wCol);
  campo(L + 3, yc, "Teléfono:", cli.telefono, wCol);
  let ye = yBox + 13;
  ye = campo(mitad + 3, ye, "Destinatario:", env.destinatario, wCol);
  ye = campo(mitad + 3, ye, "Dirección:", env.direccion, wCol);
  ye = campo(mitad + 3, ye, "Teléfono:", env.telefono, wCol);
  campo(mitad + 3, ye, "Ciudad / Estado:", env.ciudadEstado, wCol);

  // ---- Tabla de productos (se rellena hasta 10 renglones para que se vea como forma) ----
  const renglones = (d.renglones || []).map((r, i) => [
    i + 1,
    txt(r.descripcion),
    num(r.cantidad),
    txt(r.unidad || "PZA"),
    money(r.precio),
    txt(r.observaciones),
  ]);
  for (let i = renglones.length; i < 10; i++) renglones.push([i + 1, "", "", "", "", ""]);

  doc.autoTable({
    startY: yBox + hBox + 5,
    head: [["No.", "DESCRIPCIÓN DEL PRODUCTO", "CANTIDAD", "UNIDAD", "PRECIO", "OBSERVACIONES"]],
    body: renglones,
    theme: "grid",
    margin: { left: L, right: 210 - R },
    styles: { fontSize: 8.5, textColor: NEGRO, lineColor: NEGRO, lineWidth: 0.3, cellPadding: 2, minCellHeight: 8, valign: "middle" },
    headStyles: { fillColor: GRIS, textColor: NEGRO, fontStyle: "bold", halign: "center", fontSize: 8 },
    columnStyles: {
      0: { halign: "center", cellWidth: 12 },
      1: { cellWidth: 82 },
      2: { halign: "right", cellWidth: 22 },
      3: { halign: "center", cellWidth: 18 },
      4: { halign: "right", cellWidth: 24 },
      5: { cellWidth: 32 },
    },
  });

  // ---- Observaciones generales + totales ----
  let yb = doc.autoTable.previous.finalY + 5;
  const hTot = 40;
  if (yb + hTot + 30 > doc.internal.pageSize.height - 10) {
    doc.addPage();
    yb = 15;
  }
  const wObs = 118;
  const xTot = L + wObs + 4;
  const wTot = R - xTot;
  doc.setLineWidth(0.4);
  doc.roundedRect(L, yb, wObs, hTot, 1.5, 1.5);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.text("OBSERVACIONES GENERALES:", L + 3, yb + 6);
  doc.setFont("helvetica", "normal");
  const obs = doc.splitTextToSize(txt(d.observaciones), wObs - 6);
  doc.text(obs.slice(0, 5), L + 3, yb + 12);

  doc.roundedRect(xTot, yb, wTot, hTot, 1.5, 1.5);
  const filasTot = [
    ["TOTAL DE PIEZAS:", num(tot.piezas)],
    ["PRECIO:", money(tot.precio)],
    ["MODELO:", txt(tot.modelos)],
    ["TOTAL:", money(tot.total)],
  ];
  const hFila = hTot / filasTot.length;
  const xDiv = xTot + wTot * 0.55;
  doc.line(xDiv, yb, xDiv, yb + hTot);
  filasTot.forEach((f, i) => {
    const yy = yb + hFila * i;
    if (i > 0) doc.line(xTot, yy, xTot + wTot, yy);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(f[0], xTot + 3, yy + hFila / 2 + 1.5);
    doc.setFont("helvetica", "normal");
    doc.text(f[1], xTot + wTot - 3, yy + hFila / 2 + 1.5, { align: "right" });
  });

  // ---- Firmas ----
  const yf = yb + hTot + 22;
  doc.setLineWidth(0.4);
  doc.line(L + 10, yf, L + 80, yf);
  doc.line(R - 80, yf, R - 10, yf);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("ELABORÓ", L + 45, yf + 5, { align: "center" });
  doc.text("RECIBIÓ", R - 45, yf + 5, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(d.elaboro ? txt(d.elaboro) : "Nombre y Firma", L + 45, yf + 9.5, { align: "center" });
  doc.text("Nombre y Firma", R - 45, yf + 9.5, { align: "center" });

  doc.setFontSize(6.5);
  doc.text("ESTE DOCUMENTO NO REPRESENTA UN COMPROBANTE FISCAL", 105, doc.internal.pageSize.height - 8, { align: "center" });

  doc.save(d.nombreArchivo || `Nota-${txt(d.folio)}.pdf`);
}

export default notaRemisionPDF;
